import { config } from '../config';
import type { RadioPlayer } from '../audio/radio';
import type { BattleView } from '../game/battleFeed';
import type { GuideHint } from '../game/guide';
import type { SessionSnapshot } from '../game/session';
import type { ServerStatus } from '../net/serverClient';
import { BattleOverlay } from './BattleOverlay';
import { serverLabel } from './menu/serverLabel';
import type { Settings } from './settings';
import type { CardRole, FighterId, SupportCardId } from '@archon3d/core';
import { CardBattleOverlay } from './CardBattleOverlay';
import { MusterOverlay } from './MusterOverlay';
import { HandOverOverlay } from './HandOverOverlay';
import type { HandOver } from './handOver';
import { endReason, matchSummary, resultTitle } from '../game/matchSummary';
import { sideName } from '../game/sideName';
import { DrawOfferDialog } from './DrawOfferDialog';

interface Props {
  snapshot: SessionSnapshot;
  xrSupported: boolean | null;
  xrError: string | null;
  onEnterVr: () => void;
  /** Opens the home screen over the game. */
  onMenu: () => void;
  /** Opens the rule book directly from play on a screen or phone. */
  onRules: () => void;
  server: ServerStatus;
  /** The duel playing in the scene, if any; it replaces the board view's chrome. */
  battle: BattleView | null;
  onForfeit: (side?: FighterId) => void;
  /** The duel's outcome has been read. */
  onContinueOutcome?: () => void;
  /** The guided first game's lesson on show, the same as on the sign beyond the table. */
  guide: GuideHint | null;
  onGuideDismiss: () => void;
  radio: RadioPlayer;
  sound: Settings['sound'];
  onSoundChange: (sound: Settings['sound']) => void;
  onCardSupport: (card: SupportCardId | null, role?: CardRole) => void;
  /** The people here have read a Card clash's revealed cards. */
  onCardContinue: () => void;
  onMuster: (pieceId: number | null) => void;
  /** People taking turns at this device: the one waiting to take it, if any. */
  handOver?: HandOver | null;
  onHandOverReady?: () => void;
  /** After the last move: the same game again (none online, where the room decides), or another. */
  onRematch?: () => void;
  onNewGame: () => void;
  /** Whether the person on turn here may offer a draw now (D-46), and doing so. */
  drawOfferable?: boolean;
  onOfferDraw?: () => void;
  /** A person here answers a draw offered to their side. */
  onAnswerDraw?: (accept: boolean) => void;
}

/**
 * The little that stays in the DOM during a game: the name plate with the menu and VR buttons,
 * and the status line. The game's information and controls live on the wooden tablets on the
 * table, so they are the same on a screen and in the headset.
 */
export function Overlay({
  snapshot,
  onMenu,
  onRules,
  server,
  battle,
  onForfeit,
  onContinueOutcome,
  guide,
  onGuideDismiss,
  onCardSupport,
  onCardContinue,
  onMuster,
  handOver,
  onHandOverReady,
  onRematch,
  onNewGame,
  drawOfferable = false,
  onOfferDraw,
  onAnswerDraw,
}: Props) {
  if (battle)
    return <BattleOverlay battle={battle} onForfeit={onForfeit} onContinue={onContinueOutcome} />;
  // Before a hand of cards or a call is shown: the next person has to have the device first.
  if (handOver && onHandOverReady)
    return <HandOverOverlay snapshot={snapshot} handOver={handOver} onReady={onHandOverReady} />;
  if (snapshot.muster)
    return (
      <MusterOverlay
        snapshot={snapshot}
        muster={snapshot.muster}
        onRespond={onMuster}
        onMenu={onMenu}
        onRules={onRules}
      />
    );
  if (snapshot.cardClash)
    return (
      <CardBattleOverlay
        snapshot={snapshot}
        clash={snapshot.cardClash}
        onCommit={onCardSupport}
        onContinue={onCardContinue}
        onMenu={onMenu}
        onRules={onRules}
      />
    );
  const { ctx, draw } = snapshot;
  const summary = matchSummary(ctx, snapshot.state, snapshot.names);
  // A person playing here may offer a draw on their turn, once a turn (D-46).
  const offering = onOfferDraw && snapshot.state.phase !== 'ended' && !snapshot.online?.spectator;

  return (
    <div className="overlay">
      <div className="top-bar">
        <div className="column">
          <section className="plate" aria-label="RPG-Arena">
            <h1 className="title">RPG-Arena</h1>
            {/* The game being played, by its own name (the owner, 2026-09-26); its board's name only
                where a table has no game's definition. */}
            <p className="subtitle" data-testid="game-name">
              {ctx.definition?.name ?? ctx.board.def.name}
            </p>
            <div className="row">
              <button onClick={onMenu} data-testid="open-menu">
                Menu
              </button>
              <button onClick={onRules} data-testid="open-rules">
                Rules
              </button>
              {offering && (
                <button
                  onClick={onOfferDraw}
                  disabled={!drawOfferable}
                  title="On your own turn, once a turn"
                  data-testid="offer-draw"
                >
                  Offer draw
                </button>
              )}
            </div>
            {draw && !draw.asking && (
              <p className="hint" data-testid="draw-waiting">
                Draw offered · waiting for{' '}
                {draw.waiting.map((side) => sideName(snapshot, side)).join(', ')}
              </p>
            )}
            {!draw && snapshot.lastEvent?.type === 'draw' && (
              // The answer, until the next thing happens: a guided game's lesson hides it on the sign.
              <p className="hint" data-testid="draw-news">
                {snapshot.lastEvent.text}.
              </p>
            )}
          </section>
          {summary && (
            // The game is over: who won and how, and the way on (x-player-journey#4). The last
            // position stays on the table behind it.
            <section className="plate result" aria-label="Result" data-testid="game-result">
              <h2>{resultTitle(summary)}</h2>
              <p>{endReason(summary)}</p>
              <p className="result-turn">
                Turn {summary.turn} · round {summary.round}
              </p>
              <ul className="result-sides">
                {summary.sides.map((side) => (
                  <li key={side.id}>
                    <span className="result-side">{side.name}</span>{' '}
                    {side.eliminated ? 'off the board' : `${side.standing} standing`} · {side.lost}{' '}
                    lost
                  </li>
                ))}
              </ul>
              <div className="row">
                {onRematch && (
                  <button className="primary" onClick={onRematch} data-testid="result-rematch">
                    Rematch
                  </button>
                )}
                <button onClick={onNewGame} data-testid="result-new-game">
                  New game
                </button>
              </div>
            </section>
          )}
          {guide && snapshot.state.phase !== 'ended' && (
            <section
              className="plate guide"
              aria-label="Guided first game"
              data-testid="guide-hint"
            >
              <h2>{guide.title}</h2>
              <p>{guide.text}</p>
              <button onClick={onGuideDismiss} data-testid="guide-dismiss">
                Got it
              </button>
            </section>
          )}
        </div>
      </div>

      <div className="column right">
        <span />
        <div className="footer">
          <span className="pill" data-testid="server-status">
            <span className={`dot ${serverDot(server)}`} />
            {serverLabel(server)}
          </span>
          <span className="version">
            v{config.version} · {config.commit}
          </span>
        </div>
      </div>
      {draw?.asking && onAnswerDraw && (
        <DrawOfferDialog
          snapshot={snapshot}
          draw={{ ...draw, asking: draw.asking }}
          onAnswer={onAnswerDraw}
        />
      )}
    </div>
  );
}

function serverDot(server: ServerStatus): string {
  switch (server.state) {
    case 'online':
      return 'ok';
    case 'offline':
      return 'bad';
    case 'unconfigured':
      return '';
    default:
      return 'warn';
  }
}
